import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Save, Smartphone, Monitor, Plus, Trash2,
  X, QrCode, ExternalLink,
  Image as ImageIcon, Type, Music, MapPin, Send,
  Loader2, Camera, Palette, ChevronDown, ChevronUp,
  Link as LinkIcon, Play, Mail,
  Globe, Layers, Search, Star, ShoppingBag,
  Calendar, Video, Settings,
  FileText, Minus, Code,
  HelpCircle, Timer, Eye, EyeOff, BadgeCheck, BarChart3, Lock,
  GripVertical,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core'
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import api from '../../lib/axios'
import TemplateDesignPanel from '../../components/design/TemplateDesignPanel'
import VideoEmbed from '../../components/VideoEmbed'
import { ICON_CATEGORIES, getPlatform } from '../../lib/platforms'
import { detectPlatform, getVideoEmbed } from '../../lib/media'
// ⚠ Adaptez ce chemin au dossier où se trouve votre PublicPage.jsx
import PagePublic from '../public/PublicPage'
import SocialLink from '../../components/SocialLink'

/* ============================================
   BLOCS
   ============================================ */
const BLOCK_TYPES = [
  { id: 'header', label: 'Titre', icon: Type, color: 'bg-blue-50 text-blue-600' },
  { id: 'link', label: 'Lien', icon: LinkIcon, color: 'bg-teal-50 text-teal-600' },
  { id: 'image', label: 'Image', icon: ImageIcon, color: 'bg-pink-50 text-pink-600' },
  { id: 'video', label: 'Vidéo', icon: Video, color: 'bg-red-50 text-red-600' },
  { id: 'music', label: 'Musique', icon: Music, color: 'bg-green-50 text-green-600' },
  { id: 'social', label: 'Réseaux', icon: Globe, color: 'bg-purple-50 text-purple-600' },
  { id: 'map', label: 'Localisation', icon: MapPin, color: 'bg-orange-50 text-orange-600' },
  { id: 'contact', label: 'Formulaire', icon: Send, color: 'bg-indigo-50 text-indigo-600' },
  { id: 'email_capture', label: 'Capture email', icon: Mail, color: 'bg-amber-50 text-amber-600' },
  { id: 'shop', label: 'Produit', icon: ShoppingBag, color: 'bg-emerald-50 text-emerald-600' },
  { id: 'booking', label: 'Réservation', icon: Calendar, color: 'bg-cyan-50 text-cyan-600' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, color: 'bg-yellow-50 text-yellow-600' },
  { id: 'countdown', label: 'Compte à rebours', icon: Timer, color: 'bg-rose-50 text-rose-600' },
  { id: 'review', label: 'Avis', icon: Star, color: 'bg-fuchsia-50 text-fuchsia-600' },
  { id: 'embed', label: 'Intégration', icon: Code, color: 'bg-slate-100 text-slate-600' },
  { id: 'pdf', label: 'Document', icon: FileText, color: 'bg-sky-50 text-sky-600' },
  { id: 'text_divider', label: 'Séparateur texte', icon: Layers, color: 'bg-slate-100 text-slate-500' },
  { id: 'separator', label: 'Espace', icon: Minus, color: 'bg-slate-100 text-slate-600' },
]

/* ============================================
   THÈMES
   ============================================ */
const THEMES = [
  { id: 'classic', name: 'Classique', bg: '#0F1B3D', accent: '#14B8A6', text: '#FFFFFF' },
  { id: 'midnight', name: 'Midnight', bg: '#000000', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'ocean', name: 'Océan', bg: 'linear-gradient(135deg, #0891b2, #0F1B3D)', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'sunset', name: 'Sunset', bg: 'linear-gradient(135deg, #FF6B6B, #FFD93D)', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'forest', name: 'Forêt', bg: 'linear-gradient(135deg, #065f46, #022c22)', accent: '#FCD34D', text: '#FFFFFF' },
  { id: 'rose', name: 'Rose', bg: 'linear-gradient(135deg, #F472B6, #EC4899)', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'purple', name: 'Violet', bg: 'linear-gradient(135deg, #8B5CF6, #4C1D95)', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'light', name: 'Clair', bg: '#F9FAFB', accent: '#0F1B3D', text: '#0F1B3D' },
  { id: 'gold', name: 'Gold', bg: 'linear-gradient(135deg, #78350f, #c9a24a)', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'teal', name: 'Teal', bg: '#006b5f', accent: '#FFFFFF', text: '#FFFFFF' },
  { id: 'elegant', name: 'Élégant', bg: '#1A1A1A', accent: '#c9a24a', text: '#FFFFFF' },
  { id: 'candy', name: 'Candy', bg: 'linear-gradient(135deg, #F9A8D4, #C4B5FD)', accent: '#FFFFFF', text: '#FFFFFF' },
]

const FONTS = [
  { id: 'inter', name: 'Inter', family: "'Inter', sans-serif" },
  { id: 'jakarta', name: 'Plus Jakarta', family: "'Plus Jakarta Sans', sans-serif" },
  { id: 'georgia', name: 'Georgia', family: 'Georgia, serif' },
  { id: 'playfair', name: 'Playfair', family: "'Playfair Display', serif" },
  { id: 'mono', name: 'Monospace', family: "'Courier New', monospace" },
  { id: 'raleway', name: 'Raleway', family: "'Raleway', sans-serif" },
]

const BUTTON_SHAPES = [
  { id: 'rounded', name: 'Arrondi', radius: '16px' },
  { id: 'pill', name: 'Pilule', radius: '999px' },
  { id: 'square', name: 'Carré', radius: '8px' },
  { id: 'sharp', name: 'Net', radius: '0px' },
]

/* ============================================
   DESIGN (Minimal / Creative / Bold) + LAYOUTS
   ============================================ */
const DESIGN_STYLES = [
  { id: 'minimal', name: 'Minimal' },
  { id: 'creative', name: 'Creative' },
  { id: 'bold', name: 'Bold' },
]

const AVATAR_RADIUS = { circle: '9999px', rounded: '24px', square: '0px' }

const DEFAULT_SETTINGS = {
  theme: 'classic',
  customColors: false,
  customBg: '#0F1B3D',
  customAccent: '#14B8A6',
  customText: '#FFFFFF',
  bgImage: '',
  bgOverlay: 40,
  coverImage: '',
  designStyle: 'minimal',
  headerLayout: 1,
  font: 'inter',
  buttonShape: 'rounded',
  buttonStyle: 'solid',
  socialStyle: 'brand',   // brand | outline | glass | minimal | icon
  socialShape: 'rounded', // rounded | pill | square | sharp
  avatarShape: 'circle',
  textAlign: 'center',
  verifiedBadge: false,
  passwordProtected: false,
  pagePassword: '',
  seoTitle: '',
  seoDescription: '',
}

const emptyBlockDefaults = {
  header:        { title: 'Titre', subtitle: '' },
  link:          { title: 'Nouveau lien', subtitle: '', url: 'https://', icon: 'website', clicks: 0 },
  image:         { url: '', caption: '' },
  video:         { url: '' },
  music:         { url: '', platform: 'spotify' },
  social:        { platform: 'instagram', url: '' },
  map:           { address: '' },
  contact:       { title: 'Contactez-moi', subtitle: 'Laissez vos coordonnées' },
  email_capture: { title: 'Rejoins ma newsletter', subtitle: '', buttonLabel: "S'inscrire" },
  shop:          { name: '', price: '', currency: 'EUR', image: '', checkoutUrl: '' },
  booking:       { title: 'Réserver un appel', subtitle: '30 minutes', calendarUrl: '' },
  faq:           { items: [{ q: '', a: '' }] },
  countdown:     { title: 'Offre limitée', targetDate: '', buttonUrl: '' },
  review:        { author: '', text: '', rating: 5 },
  embed:         { embedUrl: '' },
  pdf:           { url: '', label: '' },
  text_divider:  { label: '' },
  separator:     { size: 'md' },
}

/* ============================================
   COMPOSANT PRINCIPAL
   ============================================ */
export default function PageEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id || id === 'new'

  const [blocks, setBlocks] = useState([
    { id: 'header-1', type: 'header', content: { title: 'Mon Profil', subtitle: 'Bienvenue sur ma page' } },
  ])
  const [selectedBlockId, setSelectedBlockId] = useState(null)
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(!isNew)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isUploadingBlock, setIsUploadingBlock] = useState(false)
  const [isUploadingBg, setIsUploadingBg] = useState(false)
  const [isUploadingCover, setIsUploadingCover] = useState(false)
  const [previewMode, setPreviewMode] = useState('mobile')
  const [iconSearch, setIconSearch] = useState('')
  const [activeTab, setActiveTab] = useState('content')
  const [showPagePassword, setShowPagePassword] = useState(false)
  // < lg : un seul panneau à la fois (éditer OU aperçu). >= lg : les deux côte à côte.
  const [mobileView, setMobileView] = useState('edit')

  const avatarInputRef = useRef(null)
  const blockImageInputRef = useRef(null)
  const bgInputRef = useRef(null)
  const coverInputRef = useRef(null)

  const [pageInfo, setPageInfo] = useState({
    title: 'Mon Profil',
    slug: '',
    bio: 'Découvrez mes liens et projets',
    avatar: '',
    is_published: false,
    settings: { ...DEFAULT_SETTINGS },
  })

  const [stats, setStats] = useState({ views: 0 })

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId)
  const settings = pageInfo.settings

  const currentTheme = (() => {
    if (settings.customColors) {
      return { bg: settings.customBg, accent: settings.customAccent, text: settings.customText }
    }
    const t = THEMES.find((th) => th.id === settings.theme) || THEMES[0]
    return { bg: t.bg, accent: t.accent, text: t.text }
  })()

  const currentFont = FONTS.find((f) => f.id === settings.font) || FONTS[0]

  const bgStyle = { background: currentTheme.bg }

  /* ---------- Drag & drop ---------- */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    setBlocks((prev) => {
      const from = prev.findIndex((b) => b.id === active.id)
      const to = prev.findIndex((b) => b.id === over.id)
      if (from === -1 || to === -1) return prev
      return arrayMove(prev, from, to)
    })
  }

  /* ---------- Chargement de la page ---------- */
  const loadPage = useCallback(async () => {
    try {
      const { data } = await api.get(`/pages/${id}`)
      const page = data.data
      const saved = page.settings || {}

      setPageInfo({
        title: page.title || '',
        slug: page.slug || '',
        bio: page.bio || '',
        avatar: page.avatar || '',
        is_published: page.is_published,
        settings: { ...DEFAULT_SETTINGS, ...saved },
      })

      let loadedBlocks =
        Array.isArray(saved.blocks) && saved.blocks.length > 0
          ? saved.blocks
          : null

      if (!loadedBlocks && page.links?.length) {
        loadedBlocks = page.links.map((link) => ({
          id: `link-${link.id}`,
          dbId: link.id,
          type: 'link',
          content: {
            title: link.title,
            url: link.url,
            icon: link.icon || 'website',
            clicks: link.clicks || 0,
          },
        }))
      }

      if (loadedBlocks) {
        setBlocks(loadedBlocks)
      }

      setStats({ views: page.views || 0 })
    } catch (err) {
      console.error(err)
      setError('Impossible de charger la page.')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (!isNew) {
      loadPage()
    }
  }, [isNew, loadPage])

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploadingAvatar(true)
    try {
      const formData = new FormData()
      formData.append('file', file); formData.append('type', 'avatar')
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      setPageInfo((prev) => ({ ...prev, avatar: data.url }))
      setSuccess('Avatar uploadé !'); setTimeout(() => setSuccess(''), 3000)
    } catch (err) { setError('Erreur upload.') } finally { setIsUploadingAvatar(false); e.target.value = '' }
  }

  const handleBlockImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selectedBlock) return
    setIsUploadingBlock(true)
    try {
      const formData = new FormData()
      formData.append('file', file); formData.append('type', 'image')
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      const field = selectedBlock.type === 'shop' ? 'image' : 'url'
      updateBlock(selectedBlock.id, field, data.url)
      setSuccess('Image uploadée !'); setTimeout(() => setSuccess(''), 3000)
    } catch (err) { setError('Erreur upload.') } finally { setIsUploadingBlock(false); e.target.value = '' }
  }

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploadingBg(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'bg')
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      updateSettings('bgImage', data.url)
      setSuccess('Image de fond uploadée !')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Erreur upload image de fond.')
    } finally {
      setIsUploadingBg(false)
      e.target.value = ''
    }
  }

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploadingCover(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', 'cover')
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      updateSettings('coverImage', data.url)
      setSuccess('Cover uploadée !')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError('Erreur upload cover.')
    } finally {
      setIsUploadingCover(false)
      e.target.value = ''
    }
  }

  const addBlock = (type) => {
    const newBlock = { id: `${type}-${Date.now()}`, type, content: { ...(emptyBlockDefaults[type] || {}) } }
    setBlocks([...blocks, newBlock])
    setSelectedBlockId(newBlock.id)
    setShowAddMenu(false)
  }

  const removeBlock = (blockId) => {
    if (!window.confirm('Supprimer ce bloc ?')) return
    setBlocks(blocks.filter((b) => b.id !== blockId))
    if (selectedBlockId === blockId) setSelectedBlockId(null)
  }

  const moveBlock = (blockId, direction) => {
    const index = blocks.findIndex((b) => b.id === blockId)
    if (index === -1) return
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= blocks.length) return
    setBlocks(arrayMove(blocks, index, newIndex))
  }

  const updateBlock = (blockId, field, value) => {
    setBlocks((prev) => prev.map((b) => b.id === blockId ? { ...b, content: { ...b.content, [field]: value } } : b))
  }

  const updateSettings = (key, value) => {
    setPageInfo((prev) => ({ ...prev, settings: { ...prev.settings, [key]: value } }))
  }

  const handleSave = async () => {
    setError(''); setSuccess(''); setIsSaving(true)
    try {
      const payload = {
        title: pageInfo.title, bio: pageInfo.bio,
        slug: pageInfo.slug || undefined,
        is_published: pageInfo.is_published,
        avatar: pageInfo.avatar || null,
        settings: { ...pageInfo.settings, blocks },
      }
      let pageId = id
      if (isNew) {
        const { data } = await api.post('/pages', { type: 'bio', ...payload })
        pageId = data.data.id
      } else {
        await api.put(`/pages/${id}`, payload)
      }
      const linkBlocks = blocks.filter((b) => b.type === 'link')
      for (const block of linkBlocks) {
        const linkPayload = {
          title: block.content.title, url: block.content.url,
          icon: block.content.icon || 'website',
          position: blocks.indexOf(block),
        }
        if (block.dbId) await api.put(`/links/${block.dbId}`, linkPayload)
        else {
          const { data } = await api.post(`/pages/${pageId}/links`, linkPayload)
          block.dbId = data.data.id
        }
      }
      setSuccess('✅ Page sauvegardée !')
      if (isNew) navigate(`/app/pages/${pageId}/edit`)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur')
      setMobileView('edit')
    } finally { setIsSaving(false) }
  }

  const publicUrl = pageInfo.slug ? `${window.location.origin}/${pageInfo.slug}` : `${window.location.origin}/votre-slug`

  const filteredIcons = useMemo(() => {
    if (!iconSearch.trim()) return ICON_CATEGORIES
    const q = iconSearch.toLowerCase()
    const out = {}
    for (const [cat, list] of Object.entries(ICON_CATEGORIES)) {
      const f = list.filter((p) => p.label.toLowerCase().includes(q))
      if (f.length) out[cat] = f
    }
    return out
  }, [iconSearch])

  const totalClicks = blocks.filter((b) => b.type === 'link').reduce((s, b) => s + (b.content.clicks || 0), 0)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9ff]">
        <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-[#f9f9ff] flex" style={{ fontFamily: currentFont.family }}>
      {/* ═══ SIDEBAR GAUCHE ═══ */}
      <aside className={`${mobileView === 'preview' ? 'hidden' : 'flex'} lg:flex w-full lg:w-[400px] bg-white lg:border-r border-slate-100 flex-col fixed left-0 top-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 z-20`}>
        <div className="p-3 sm:p-4 border-b border-slate-100 flex items-center justify-between gap-2">
          <button onClick={() => navigate('/app/pages')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-navy">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <button onClick={handleSave} disabled={isSaving} className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-lg font-semibold text-xs transition-colors disabled:opacity-60">
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Enregistrer
          </button>
        </div>

        <div className="flex border-b border-slate-100 px-2 sm:px-3 gap-1 overflow-x-auto scrollbar-thin">
          {[
            { id: 'content', label: 'Contenu', icon: Type },
            { id: 'design', label: 'Design', icon: Palette },
            { id: 'settings', label: 'Paramètres', icon: Settings },
            { id: 'stats', label: 'Stats', icon: BarChart3 },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-xs font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${activeTab === tab.id ? 'border-[#006b5f] text-[#006b5f]' : 'border-transparent text-slate-500'}`}>
              <tab.icon className="w-3.5 h-3.5" />{tab.label}
            </button>
          ))}
        </div>

        {(error || success) && (
          <div className="p-3 space-y-2">
            {error && <div className="p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">{error}</div>}
            {success && <div className="p-2 bg-teal-50 border border-teal-100 rounded-lg text-xs text-teal-700">{success}</div>}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'content' && (
            <div className="p-4 space-y-5 mx-auto w-full max-w-2xl lg:max-w-none">
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Profil</h3>

                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className={`w-20 h-20 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center overflow-hidden ${settings.avatarShape === 'circle' ? 'rounded-full' : settings.avatarShape === 'rounded' ? 'rounded-2xl' : 'rounded-none'}`}>
                      {pageInfo.avatar ? <img src={pageInfo.avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl font-bold text-white">{pageInfo.title?.charAt(0)?.toUpperCase() || 'T'}</span>}
                    </div>
                    <button onClick={() => avatarInputRef.current?.click()} disabled={isUploadingAvatar}
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
                      {isUploadingAvatar ? <Loader2 className="w-3 h-3 text-[#006b5f] animate-spin" /> : <Camera className="w-3 h-3 text-[#006b5f]" />}
                    </button>
                  </div>
                  <div className="text-xs text-slate-500">Cliquez sur <Camera className="w-3 h-3 inline" /> pour changer</div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-navy mb-1">Nom</label>
                    <input type="text" value={pageInfo.title} onChange={(e) => setPageInfo({ ...pageInfo, title: e.target.value })}
                      placeholder="Votre nom"
                      className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20 focus:bg-white transition-all" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-navy mb-1">Bio</label>
                    <textarea rows={3} value={pageInfo.bio} onChange={(e) => setPageInfo({ ...pageInfo, bio: e.target.value })}
                      placeholder="Décrivez-vous..."
                      className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20 focus:bg-white resize-none transition-all" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={settings.verifiedBadge} onChange={(e) => updateSettings('verifiedBadge', e.target.checked)} className="w-4 h-4 rounded accent-[#006b5f]" />
                    <span className="text-xs font-semibold text-navy">Badge vérifié à côté du nom</span>
                  </label>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Blocs</h3>
                  <button onClick={() => setShowAddMenu(true)} className="text-xs text-[#006b5f] font-semibold hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" />Ajouter
                  </button>
                </div>

                <div className="space-y-2">
                  {blocks.length === 0 ? (
                    <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                      <Plus className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-500">Aucun bloc</p>
                    </div>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                        {blocks.map((block, index) => (
                          <SortableBlock key={block.id} id={block.id}>
                            {(handle) => (
                              <BlockEditorInline block={block}
                                dragHandleProps={handle}
                                onUpdate={(field, value) => updateBlock(block.id, field, value)}
                                onRemove={() => removeBlock(block.id)}
                                onMoveUp={() => moveBlock(block.id, 'up')}
                                onMoveDown={() => moveBlock(block.id, 'down')}
                                onUploadImage={() => { setSelectedBlockId(block.id); blockImageInputRef.current?.click() }}
                                onOpenIconPicker={() => { setSelectedBlockId(block.id); setShowIconPicker(true) }}
                                canMoveUp={index > 0} canMoveDown={index < blocks.length - 1}
                                isUploading={isUploadingBlock && selectedBlockId === block.id} />
                            )}
                          </SortableBlock>
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'design' && (
            <div className="p-4 space-y-5 mx-auto w-full max-w-2xl lg:max-w-none">
              {/* ═══ TEMPLATES + DIMENSIONS + EN-TÊTE + LIENS ═══ */}
              <TemplateDesignPanel
                settings={settings}
                onChange={(patch) => setPageInfo((prev) => ({ ...prev, settings: { ...prev.settings, ...patch } }))}
              />

              {/* ═══ IMAGES (avatar + cover) ═══ */}
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-4">Images</h3>
              <div className="flex gap-3">
                <button onClick={() => avatarInputRef.current?.click()} disabled={isUploadingAvatar}
                  className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  {isUploadingAvatar
                    ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    : pageInfo.avatar
                      ? <img src={pageInfo.avatar} alt="" className="w-full h-full object-cover" />
                      : <Camera className="w-5 h-5 text-slate-400" />}
                </button>
                <button onClick={() => coverInputRef.current?.click()} disabled={isUploadingCover}
                  className="flex-1 h-20 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                  {isUploadingCover
                    ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    : settings.coverImage
                      ? <img src={settings.coverImage} alt="" className="w-full h-full object-cover" />
                      : <Camera className="w-5 h-5 text-slate-400" />}
                </button>
              </div>
              {settings.coverImage && (
                <button onClick={() => updateSettings('coverImage', '')} className="text-[11px] text-red-500 font-semibold">
                  Retirer la cover
                </button>
              )}

              {/* ═══ DESIGN : 3 styles + 4 layouts ═══ */}
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Design</h3>
              <div className="grid grid-cols-3 gap-2">
                {DESIGN_STYLES.map((s) => (
                  <button key={s.id} onClick={() => updateSettings('designStyle', s.id)}
                    className={`py-3 rounded-xl border text-sm font-semibold transition-colors ${settings.designStyle === s.id ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]' : 'bg-white border-slate-200 text-slate-700'}`}>
                    {s.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-2 max-w-sm lg:max-w-none">
                {[1, 2, 3, 4].map((n) => (
                  <LayoutThumb key={n} layout={n} active={Number(settings.headerLayout) === n} onClick={() => updateSettings('headerLayout', n)} />
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Thème</h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2">
                {THEMES.map((t) => (
                  <button key={t.id} onClick={() => { updateSettings('theme', t.id); updateSettings('customColors', false) }}
                    className={`aspect-square rounded-xl border-2 transition-all ${settings.theme === t.id && !settings.customColors ? 'border-[#006b5f] scale-105' : 'border-transparent hover:border-slate-200'}`}
                    style={{ background: t.bg }}
                    title={t.name} />
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Couleurs custom</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={settings.customColors} onChange={(e) => updateSettings('customColors', e.target.checked)} className="w-4 h-4 rounded accent-[#006b5f]" />
                <span className="text-xs font-semibold text-navy">Activer</span>
              </label>
              {settings.customColors && (
                <div className="space-y-3">
                  <ColorInput label="Fond" value={settings.customBg} onChange={(v) => updateSettings('customBg', v)} />
                  <ColorInput label="Accent" value={settings.customAccent} onChange={(v) => updateSettings('customAccent', v)} />
                  <ColorInput label="Texte" value={settings.customText} onChange={(v) => updateSettings('customText', v)} />
                </div>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Image de fond</h3>

              {settings.bgImage && (
                <div className="relative rounded-lg overflow-hidden border border-slate-200">
                  <img src={settings.bgImage} alt="bg" className="w-full h-24 object-cover" />
                  <button
                    onClick={() => updateSettings('bgImage', '')}
                    className="absolute top-1.5 right-1.5 p-1 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors"
                    title="Retirer l'image"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              <button
                onClick={() => bgInputRef.current?.click()}
                disabled={isUploadingBg}
                className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#006b5f] text-xs text-slate-500 font-semibold transition-colors disabled:opacity-60"
              >
                {isUploadingBg
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Upload...</>
                  : <><Camera className="w-3.5 h-3.5" /> {settings.bgImage ? "Changer l'image" : 'Uploader une image'}</>}
              </button>

              {settings.bgImage && (
                <div>
                  <label className="block text-xs font-semibold text-navy mb-1">
                    Assombrir le fond : {settings.bgOverlay ?? 40}%
                  </label>
                  <input
                    type="range" min="0" max="90"
                    value={settings.bgOverlay ?? 40}
                    onChange={(e) => updateSettings('bgOverlay', Number(e.target.value))}
                    className="w-full accent-[#006b5f]"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Ajuste la lisibilité du texte sur l'image.</p>
                </div>
              )}

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Police</h3>
              <div className="space-y-2">
                {FONTS.map((font) => (
                  <button key={font.id} onClick={() => updateSettings('font', font.id)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${settings.font === font.id ? 'border-[#006b5f] bg-[#f0faf8]' : 'border-slate-200 hover:border-slate-300'}`}
                    style={{ fontFamily: font.family }}>
                    <p className="text-sm font-semibold text-navy">{font.name}</p>
                  </button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Forme boutons</h3>
              <div className="grid grid-cols-4 gap-2">
                {BUTTON_SHAPES.map((shape) => (
                  <button key={shape.id} onClick={() => updateSettings('buttonShape', shape.id)}
                    className={`aspect-square border-2 transition-all flex items-center justify-center ${settings.buttonShape === shape.id ? 'border-[#006b5f] bg-[#f0faf8]' : 'border-slate-200'}`}
                    style={{ borderRadius: shape.radius }}>
                    <div className="w-6 h-6 bg-navy" style={{ borderRadius: shape.radius }} />
                  </button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Style boutons</h3>
              <div className="grid grid-cols-3 gap-2">
                {[{ id: 'solid', label: 'Plein' }, { id: 'outline', label: 'Contour' }, { id: 'glass', label: 'Verre' }].map((s) => (
                  <button key={s.id} onClick={() => updateSettings('buttonStyle', s.id)}
                    className={`py-2 rounded-lg border-2 text-xs font-semibold ${settings.buttonStyle === s.id ? 'border-[#006b5f] bg-[#f0faf8] text-navy' : 'border-slate-200 text-slate-500'}`}>{s.label}</button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Style réseaux</h3>
              <div className="grid grid-cols-3 gap-2">
                {[{ id: 'brand', label: 'Couleur' }, { id: 'outline', label: 'Contour' }, { id: 'glass', label: 'Verre' }, { id: 'minimal', label: 'Minimal' }, { id: 'icon', label: 'Icônes' }].map((s) => (
                  <button key={s.id} onClick={() => updateSettings('socialStyle', s.id)}
                    className={`py-2 rounded-lg border-2 text-xs font-semibold ${settings.socialStyle === s.id ? 'border-[#006b5f] bg-[#f0faf8] text-navy' : 'border-slate-200 text-slate-500'}`}>{s.label}</button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Forme réseaux</h3>
              <div className="grid grid-cols-4 gap-2">
                {BUTTON_SHAPES.map((shape) => (
                  <button key={shape.id} onClick={() => updateSettings('socialShape', shape.id)}
                    className={`aspect-square border-2 transition-all flex items-center justify-center ${settings.socialShape === shape.id ? 'border-[#006b5f] bg-[#f0faf8]' : 'border-slate-200'}`}
                    style={{ borderRadius: shape.radius }}>
                    <div className="w-6 h-6 bg-navy" style={{ borderRadius: shape.radius }} />
                  </button>
                ))}
              </div>

              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-3">Forme avatar</h3>
              <div className="grid grid-cols-3 gap-2">
                {[{ id: 'circle', label: 'Cercle' }, { id: 'rounded', label: 'Arrondi' }, { id: 'square', label: 'Carré' }].map((s) => (
                  <button key={s.id} onClick={() => updateSettings('avatarShape', s.id)}
                    className={`py-2 rounded-lg border-2 text-xs font-semibold ${settings.avatarShape === s.id ? 'border-[#006b5f] bg-[#f0faf8] text-navy' : 'border-slate-200 text-slate-500'}`}>{s.label}</button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="p-4 space-y-5 mx-auto w-full max-w-2xl lg:max-w-none">
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Slug (URL)</label>
                <input type="text" value={pageInfo.slug}
                  onChange={(e) => setPageInfo({ ...pageInfo, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') })}
                  placeholder="mon-profil"
                  className="w-full px-3 py-2.5 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
                <p className="text-[10px] text-slate-500 mt-1">{publicUrl}</p>
              </div>

              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs font-semibold text-navy">Publier la page</p>
                  <p className="text-[10px] text-slate-500">Rendre visible publiquement</p>
                </div>
                <button onClick={() => setPageInfo({ ...pageInfo, is_published: !pageInfo.is_published })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${pageInfo.is_published ? 'bg-[#006b5f]' : 'bg-slate-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${pageInfo.is_published ? 'translate-x-6' : ''}`} />
                </button>
              </label>

              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <label className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-navy flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Protéger par mot de passe</span>
                  <button onClick={() => updateSettings('passwordProtected', !settings.passwordProtected)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${settings.passwordProtected ? 'bg-[#006b5f]' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.passwordProtected ? 'translate-x-5' : ''}`} />
                  </button>
                </label>
                {settings.passwordProtected && (
                  <div className="relative">
                    <input type={showPagePassword ? 'text' : 'password'} value={settings.pagePassword}
                      onChange={(e) => updateSettings('pagePassword', e.target.value)} placeholder="Mot de passe"
                      className="w-full px-3 py-2 pr-9 bg-white rounded-lg text-sm border border-slate-200" />
                    <button onClick={() => setShowPagePassword((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPagePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>

              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-navy">Référencement (SEO)</p>
                <input value={settings.seoTitle} onChange={(e) => updateSettings('seoTitle', e.target.value)} placeholder="Titre pour Google / réseaux"
                  className="w-full px-3 py-2 bg-white rounded-lg text-sm border border-slate-200" />
                <textarea rows={2} value={settings.seoDescription} onChange={(e) => updateSettings('seoDescription', e.target.value)} placeholder="Description"
                  className="w-full px-3 py-2 bg-white rounded-lg text-sm border border-slate-200 resize-none" />
              </div>

              <button onClick={() => setShowQrModal(true)} className="w-full flex items-center justify-center gap-2 py-3 bg-slate-100 rounded-lg text-navy font-semibold text-sm hover:bg-slate-200 transition-colors">
                <QrCode className="w-4 h-4" /> Voir le QR Code
              </button>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="p-4 space-y-4 mx-auto w-full max-w-2xl lg:max-w-none">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-2xl font-extrabold text-navy">{stats.views}</p>
                  <p className="text-[11px] text-slate-500 mt-1">Vues de la page</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-2xl font-extrabold text-navy">{totalClicks}</p>
                  <p className="text-[11px] text-slate-500 mt-1">Clics totaux</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Clics par lien</p>
                <div className="space-y-1.5">
                  {blocks.filter((b) => b.type === 'link').map((b) => (
                    <div key={b.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs">
                      <span className="text-slate-600 truncate">{b.content.title || 'Lien'}</span>
                      <span className="font-semibold text-navy">{b.content.clicks || 0}</span>
                    </div>
                  ))}
                  {blocks.filter((b) => b.type === 'link').length === 0 && <p className="text-xs text-slate-400">Aucun lien pour l'instant.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ═══ APERÇU LIVE ═══ */}
      <main className={`${mobileView === 'edit' ? 'hidden' : 'block'} lg:block flex-1 min-w-0 lg:ml-[400px] pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-0`}>
        <div className="sticky top-14 lg:top-0 z-10 bg-white border-b border-slate-100 px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded-lg transition-colors ${previewMode === 'mobile' ? 'bg-white shadow-sm text-[#006b5f]' : 'text-slate-500'}`}>
                <Smartphone className="w-4 h-4" />
              </button>
              <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded-lg transition-colors ${previewMode === 'desktop' ? 'bg-white shadow-sm text-[#006b5f]' : 'text-slate-500'}`}>
                <Monitor className="w-4 h-4" />
              </button>
            </div>
            <span className="hidden sm:inline text-xs text-slate-500 font-medium">Aperçu en temps réel</span>
          </div>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#006b5f] font-semibold hover:underline flex items-center gap-1">
            <span className="hidden min-[400px]:inline">Voir la page publique</span><span className="min-[400px]:hidden">Page publique</span> <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="flex items-start sm:items-center justify-center py-4 sm:py-8 px-3 sm:px-6 min-h-[calc(100dvh-60px)] bg-[#f9f9ff]">
          <div className={`bg-white overflow-hidden shadow-2xl border border-slate-200 transition-all duration-300 ${previewMode === 'mobile' ? 'w-full max-w-[375px] rounded-[28px] sm:rounded-[40px]' : 'w-full max-w-3xl rounded-2xl'}`}>
            {/* Même rendu que la page publique (toutes les dispositions et templates) */}
            <PagePublic
              previewData={{
                page: { title: pageInfo.title, bio: pageInfo.bio, avatar: pageInfo.avatar, settings: pageInfo.settings },
                blocks,
                slug: pageInfo.slug,
              }}
            />
          </div>
        </div>
      </main>

      {/* ═══ BARRE DU BAS (< lg) : Éditer / Aperçu / Enregistrer ═══ */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-100 pb-[env(safe-area-inset-bottom)]">
        <div className="h-14 grid grid-cols-3 items-stretch">
          <button onClick={() => setMobileView('edit')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${mobileView === 'edit' ? 'text-[#006b5f]' : 'text-slate-500'}`}>
            <Layers className="w-5 h-5" />Éditer
          </button>
          <button onClick={() => setMobileView('preview')}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${mobileView === 'preview' ? 'text-[#006b5f]' : 'text-slate-500'}`}>
            <Eye className="w-5 h-5" />Aperçu
          </button>
          <button onClick={handleSave} disabled={isSaving}
            className="flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-white bg-[#006b5f] active:bg-[#005a50] disabled:opacity-60">
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}Enregistrer
          </button>
        </div>
      </nav>

      <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
      <input ref={blockImageInputRef} type="file" accept="image/*,.pdf" onChange={handleBlockImageUpload} className="hidden" />
      <input ref={bgInputRef} type="file" accept="image/*" onChange={handleBgUpload} className="hidden" />
      <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />

      {/* MODALS */}
      {showAddMenu && (
        <EditorModal title="Ajouter un bloc" onClose={() => setShowAddMenu(false)} size="lg">
          <div className="grid grid-cols-2 min-[400px]:grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
            {BLOCK_TYPES.map((type) => {
              const Icon = type.icon
              return (
                <button key={type.id} onClick={() => addBlock(type.id)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-100 hover:border-[#006b5f] hover:bg-[#f0faf8] transition-all">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type.color}`}><Icon className="w-5 h-5" /></div>
                  <p className="font-semibold text-navy text-xs text-center">{type.label}</p>
                </button>
              )
            })}
          </div>
        </EditorModal>
      )}

      {showIconPicker && selectedBlock && (
        <EditorModal title="Choisir une icône" onClose={() => setShowIconPicker(false)} size="lg">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Rechercher..." value={iconSearch} onChange={(e) => setIconSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
            </div>
          </div>
          <div className="max-h-[60dvh] overflow-y-auto space-y-6">
            {Object.entries(filteredIcons).map(([category, icons]) => {
              if (icons.length === 0) return null
              return (
                <div key={category}>
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{category}</h3>
                  <div className="grid grid-cols-3 min-[400px]:grid-cols-4 sm:grid-cols-5 gap-2">
                    {icons.map((iconData) => {
                      const Icon = iconData.icon
                      return (
                        <button key={iconData.id} onClick={() => {
                          const field = selectedBlock.type === 'social' || selectedBlock.type === 'music' ? 'platform' : 'icon'
                          updateBlock(selectedBlock.id, field, iconData.id); setShowIconPicker(false)
                        }}
                          className="flex flex-col items-center gap-1 p-3 rounded-xl border-2 border-slate-100 hover:border-[#006b5f] transition-all">
                          <Icon className="w-6 h-6" style={{ color: iconData.color }} />
                          <span className="text-[9px] text-slate-500 text-center">{iconData.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </EditorModal>
      )}

      {showQrModal && (
        <EditorModal title="QR Code" onClose={() => setShowQrModal(false)} icon={QrCode}>
          <div className="rounded-2xl p-4 sm:p-6 mb-4 flex items-center justify-center" style={bgStyle}>
            <div className="bg-white p-4 rounded-2xl shadow-2xl">
              <QRCodeSVG value={publicUrl} size={200} level="H" style={{ width: '100%', maxWidth: 200, height: 'auto' }} />
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
            <Globe className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-navy flex-1 truncate font-mono">{publicUrl}</span>
            <button onClick={() => navigator.clipboard.writeText(publicUrl)} className="text-xs text-[#006b5f] font-semibold">Copier</button>
          </div>
        </EditorModal>
      )}
    </div>
  )
}

/* ============================================
   HEADER PROFIL (Minimal / Creative / Bold × 4 layouts)
   (conservé pour LivePreview exporté ci-dessous)
   ============================================ */
function ProfileHeader({ pageInfo, settings, theme }) {
  const style = settings.designStyle || 'minimal'
  const layout = Number(settings.headerLayout) || 1
  const cover = settings.coverImage
  const avatarRadius = AVATAR_RADIUS[settings.avatarShape || 'circle']

  const S = {
    minimal:  { coverRadius: '0 0 50% 50% / 0 0 28px 28px', title: 'text-lg font-semibold', ring: 'rgba(255,255,255,0.35)' },
    creative: { coverRadius: '0 0 48px 48px', title: 'text-xl font-extrabold', ring: theme.accent },
    bold:     { coverRadius: '0', title: 'text-xl font-black uppercase tracking-wider', ring: theme.text },
  }[style] || {}

  const coverBg = cover
    ? { background: `url(${cover}) center/cover no-repeat` }
    : { background: 'rgba(255,255,255,0.18)' }

  const avatar = (size, rotate = 0) => (
    <div
      className="overflow-hidden flex items-center justify-center shrink-0"
      style={{
        width: size, height: size, borderRadius: avatarRadius,
        border: `4px solid ${S.ring}`, background: 'rgba(255,255,255,0.2)',
        transform: `rotate(${rotate}deg)`,
      }}
    >
      {pageInfo.avatar
        ? <img src={pageInfo.avatar} alt="" className="w-full h-full object-cover" />
        : <span className="text-2xl font-bold" style={{ color: theme.text }}>{pageInfo.title?.charAt(0)?.toUpperCase() || 'T'}</span>}
    </div>
  )

  const title = (className = '', justify = 'center') => (
    <h1 className={`${S.title} flex items-center gap-1.5 ${className}`} style={{ color: theme.text, justifyContent: justify }}>
      {pageInfo.title || 'Votre nom'}
      {settings.verifiedBadge && <BadgeCheck className="w-4 h-4 fill-current" style={{ color: theme.accent }} />}
    </h1>
  )

  const bio = (className = '') =>
    pageInfo.bio
      ? <p className={`text-xs opacity-80 whitespace-pre-line ${className}`} style={{ color: theme.text }}>{pageInfo.bio}</p>
      : null

  // Layout 1 : cover courbée + avatar centré
  if (layout === 1) return (
    <div>
      <div style={{ height: 130, borderRadius: S.coverRadius, ...coverBg }} />
      <div className="flex flex-col items-center text-center px-6 -mt-10">
        {avatar(80)}
        {title('mt-3')}
        {bio('mt-1')}
      </div>
    </div>
  )

  // Layout 2 : grand nom sur la cover + grand avatar
  if (layout === 2) return (
    <div>
      <div className="relative flex justify-center" style={{ height: 230, borderRadius: S.coverRadius, ...coverBg }}>
        <h1
          className={`absolute top-5 left-0 right-0 text-center text-3xl uppercase tracking-widest ${style === 'bold' ? 'font-black' : 'font-light'}`}
          style={{ color: theme.text, textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
          {pageInfo.title || 'NAME'}
        </h1>
        <div className="absolute -bottom-12">{avatar(110)}</div>
      </div>
      <div className="text-center px-6 pt-16">
        {settings.verifiedBadge && <BadgeCheck className="w-4 h-4 fill-current inline mb-1" style={{ color: theme.accent }} />}
        {bio()}
      </div>
    </div>
  )

  // Layout 3 : nom en haut + 2 photos inclinées
  if (layout === 3) return (
    <div className="px-6 pt-8 text-center">
      {title()}
      <div className="relative mx-auto mt-5" style={{ width: 200, height: 150 }}>
        <div className="absolute left-0 top-2">{avatar(110, -8)}</div>
        {cover && (
          <div className="absolute right-0 top-8 overflow-hidden"
            style={{
              width: 110, height: 110,
              borderRadius: avatarRadius === '9999px' ? '24px' : avatarRadius,
              border: `4px solid ${S.ring}`, transform: 'rotate(8deg)', ...coverBg,
            }} />
        )}
      </div>
      {bio('mt-2')}
    </div>
  )

  // Layout 4 : cover + petit avatar à gauche, texte à gauche
  return (
    <div>
      <div style={{ height: 110, borderRadius: S.coverRadius, ...coverBg }} />
      <div className="px-6 -mt-8 text-left">
        {avatar(64)}
        {title('mt-2', 'flex-start')}
        {bio('mt-1')}
      </div>
    </div>
  )
}

/* Miniatures du sélecteur de layout */
function LayoutThumb({ layout, active, onClick }) {
  const g = '#d4d4d8'
  return (
    <button onClick={onClick}
      className={`relative overflow-hidden bg-white rounded-2xl border-2 transition-all ${active ? 'border-black' : 'border-slate-200'}`}
      style={{ aspectRatio: '9/16' }}>
      {layout === 1 && <>
        <div style={{ height: '28%', background: g, borderRadius: '0 0 50% 50% / 0 0 16px 16px' }} />
        <div className="mx-auto -mt-4 w-8 h-8 rounded-full bg-zinc-400 border-2 border-white" />
        <div className="mx-auto mt-1 h-1 w-8 bg-zinc-300 rounded" />
      </>}
      {layout === 2 && (
        <div style={{ height: '38%', background: g, borderRadius: '0 0 16px 16px' }} className="relative">
          <div className="absolute inset-x-0 top-2 mx-auto h-1.5 w-10 bg-white/80 rounded" />
          <div className="absolute -bottom-5 inset-x-0 mx-auto w-12 h-12 rounded-full bg-zinc-300 border-2 border-white" />
        </div>
      )}
      {layout === 3 && <>
        <div className="mx-auto mt-3 h-1.5 w-8 bg-zinc-300 rounded" />
        <div className="relative mx-auto mt-4 w-16 h-14">
          <div className="absolute left-0 top-1 w-9 h-9 bg-zinc-200 rounded" style={{ transform: 'rotate(-8deg)' }} />
          <div className="absolute right-0 top-4 w-9 h-9 bg-zinc-300 rounded" style={{ transform: 'rotate(8deg)' }} />
        </div>
      </>}
      {layout === 4 && <>
        <div style={{ height: '30%', background: g, borderRadius: '0 0 16px 16px' }} />
        <div className="ml-3 -mt-3 w-6 h-6 rounded-full bg-zinc-300 border-2 border-white" />
        <div className="ml-3 mt-1 h-1 w-8 bg-zinc-300 rounded" />
      </>}
    </button>
  )
}

/* ============================================
   SOUS-COMPOSANTS
   ============================================ */
function EditorModal({ title, onClose, children, icon: Icon, size = 'md' }) {
  const maxW = size === 'lg' ? 'max-w-3xl' : 'max-w-2xl'
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className={`bg-white rounded-t-3xl sm:rounded-3xl w-full ${maxW} p-4 sm:p-6 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl max-h-[90dvh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-bold text-navy text-xl flex items-center gap-2">{Icon && <Icon className="w-5 h-5 text-[#006b5f]" />}{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

/* Wrapper drag & drop d'un bloc */
function SortableBlock({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.85 : 1,
        position: 'relative',
      }}
    >
      {children({ ...attributes, ...listeners })}
    </div>
  )
}

function ColorInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-navy mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer" />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 px-2 py-2 bg-slate-50 rounded-lg text-xs border border-slate-200 font-mono" />
      </div>
    </div>
  )
}

function BlockEditorInline({ block, dragHandleProps, onUpdate, onRemove, onMoveUp, onMoveDown, onUploadImage, onOpenIconPicker, canMoveUp, canMoveDown, isUploading }) {
  const type = BLOCK_TYPES.find((t) => t.id === block.type) || BLOCK_TYPES[0]
  const Icon = type.icon
  const c = block.content || {}
  const inputCls = "w-full px-2 py-1.5 bg-slate-50 rounded-lg text-xs border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20"

  // URL -> réseau détecté automatiquement
  const onSocialUrl = (url) => {
    onUpdate('url', url)
    const p = detectPlatform(url)
    if (p) onUpdate('platform', p)
  }
  const onLinkUrl = (url) => {
    onUpdate('url', url)
    const p = detectPlatform(url)
    if (p) onUpdate('icon', p)
  }
  const onMusicUrl = (url) => {
    onUpdate('url', url)
    const p = detectPlatform(url)
    if (p && ['spotify', 'apple', 'soundcloud', 'deezer', 'bandcamp'].includes(p)) onUpdate('platform', p)
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <button {...dragHandleProps} aria-label="Déplacer"
          className="p-1 -ml-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing touch-none">
          <GripVertical className="w-4 h-4" />
        </button>
        <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${type.color}`}><Icon className="w-3 h-3" /></div>
        <span className="text-xs font-semibold text-navy flex-1">{type.label}</span>
        <button onClick={onMoveUp} disabled={!canMoveUp} className="p-1 rounded hover:bg-slate-50 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
        <button onClick={onMoveDown} disabled={!canMoveDown} className="p-1 rounded hover:bg-slate-50 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
        <button onClick={onRemove} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3 h-3" /></button>
      </div>

      {block.type === 'header' && (
        <div className="space-y-2">
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={`${inputCls} font-bold`} />
          <input type="text" value={c.subtitle || ''} onChange={(e) => onUpdate('subtitle', e.target.value)} placeholder="Sous-titre" className={inputCls} />
        </div>
      )}

      {block.type === 'link' && (
        <div className="space-y-2">
          <button onClick={onOpenIconPicker} className="w-full flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-lg border border-slate-200 text-xs">
            {(() => { const p = getPlatform(c.icon); const PIcon = p.icon; return <><PIcon className="w-3 h-3" style={{ color: p.color }} /><span className="font-semibold text-navy">{p.label}</span></> })()}
            <ChevronDown className="w-3 h-3 text-slate-400 ml-auto" />
          </button>
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={inputCls} />
          <input type="text" value={c.subtitle || ''} onChange={(e) => onUpdate('subtitle', e.target.value)} placeholder="Sous-titre (optionnel)" className={inputCls} />
          <input type="url" value={c.url} onChange={(e) => onLinkUrl(e.target.value)} placeholder="https://" className={inputCls} />
          <label className="flex items-center gap-2 text-[11px] text-slate-500 pt-1">
            <input type="checkbox" checked={!!c.scheduled} onChange={(e) => onUpdate('scheduled', e.target.checked)} className="accent-[#006b5f]" />
            Planifier la visibilité
          </label>
          {c.scheduled && (
            <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
              <input type="datetime-local" value={c.startAt || ''} onChange={(e) => onUpdate('startAt', e.target.value)} className={inputCls} />
              <input type="datetime-local" value={c.endAt || ''} onChange={(e) => onUpdate('endAt', e.target.value)} className={inputCls} />
            </div>
          )}
          <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1">
            <span>Clics</span><span className="font-semibold text-slate-600">{c.clicks || 0}</span>
          </div>
        </div>
      )}

      {block.type === 'image' && (
        <div className="space-y-2">
          {c.url && (
            <div className="relative rounded-lg overflow-hidden">
              <img src={c.url} alt="" className="w-full h-20 object-cover" />
            </div>
          )}
          <button onClick={onUploadImage} disabled={isUploading}
            className="w-full flex items-center justify-center gap-1 py-2 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#006b5f] text-xs text-slate-500">
            {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            {isUploading ? 'Upload...' : 'Uploader'}
          </button>
          <input type="text" value={c.caption || ''} onChange={(e) => onUpdate('caption', e.target.value)} placeholder="Légende" className={inputCls} />
        </div>
      )}

      {block.type === 'video' && (
        <div className="space-y-1">
          <input type="url" value={c.url} onChange={(e) => onUpdate('url', e.target.value)}
            placeholder="YouTube, Vimeo, TikTok, Instagram, Facebook, Twitch, Loom, .mp4…" className={inputCls} />
          {c.url && (
            <p className={`text-[10px] ${getVideoEmbed(c.url) ? 'text-teal-600' : 'text-amber-600'}`}>
              {getVideoEmbed(c.url)
                ? '✓ Vidéo reconnue, elle sera lue directement sur la page'
                : 'Lien non intégrable : un bouton "Ouvrir la vidéo" sera affiché'}
            </p>
          )}
        </div>
      )}

      {block.type === 'social' && (
        <div className="space-y-2">
          <button onClick={onOpenIconPicker} className="w-full flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-lg border border-slate-200 text-xs">
            {(() => { const p = getPlatform(c.platform); const PIcon = p.icon; return <><PIcon className="w-3 h-3" style={{ color: p.color }} /><span className="font-semibold text-navy">{p.label}</span></> })()}
            <ChevronDown className="w-3 h-3 text-slate-400 ml-auto" />
          </button>
          <input type="url" value={c.url} onChange={(e) => onSocialUrl(e.target.value)} placeholder="Colle le lien (instagram.com/...)" className={inputCls} />
        </div>
      )}

      {block.type === 'map' && (
        <input type="text" value={c.address} onChange={(e) => onUpdate('address', e.target.value)} placeholder="Adresse" className={inputCls} />
      )}

      {block.type === 'separator' && (
        <select value={c.size} onChange={(e) => onUpdate('size', e.target.value)} className={inputCls}>
          <option value="sm">Petit</option><option value="md">Moyen</option><option value="lg">Grand</option>
        </select>
      )}

      {block.type === 'contact' && (
        <div className="space-y-2">
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={inputCls} />
          <input type="text" value={c.subtitle} onChange={(e) => onUpdate('subtitle', e.target.value)} placeholder="Sous-titre" className={inputCls} />
        </div>
      )}

      {block.type === 'music' && (
        <div className="space-y-2">
          <select value={c.platform === 'apple_music' ? 'apple' : c.platform} onChange={(e) => onUpdate('platform', e.target.value)} className={inputCls}>
            <option value="spotify">Spotify</option>
            <option value="apple">Apple Music</option>
            <option value="soundcloud">SoundCloud</option>
            <option value="deezer">Deezer</option>
            <option value="bandcamp">Bandcamp</option>
          </select>
          <input type="url" value={c.url} onChange={(e) => onMusicUrl(e.target.value)} placeholder="URL" className={inputCls} />
        </div>
      )}

      {block.type === 'email_capture' && (
        <div className="space-y-2">
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={inputCls} />
          <input type="text" value={c.subtitle} onChange={(e) => onUpdate('subtitle', e.target.value)} placeholder="Sous-titre" className={inputCls} />
          <input type="text" value={c.buttonLabel} onChange={(e) => onUpdate('buttonLabel', e.target.value)} placeholder="Texte du bouton" className={inputCls} />
        </div>
      )}

      {block.type === 'shop' && (
        <div className="space-y-2">
          {c.image && <img src={c.image} alt="" className="w-full h-20 object-cover rounded-lg" />}
          <button onClick={onUploadImage} disabled={isUploading}
            className="w-full flex items-center justify-center gap-1 py-2 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#006b5f] text-xs text-slate-500">
            {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
            {c.image ? "Changer l'image" : 'Photo du produit'}
          </button>
          <input type="text" value={c.name} onChange={(e) => onUpdate('name', e.target.value)} placeholder="Nom du produit" className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={c.price} onChange={(e) => onUpdate('price', e.target.value)} placeholder="Prix" className={inputCls} />
            <select value={c.currency} onChange={(e) => onUpdate('currency', e.target.value)} className={inputCls}>
              <option value="EUR">EUR</option><option value="USD">USD</option><option value="MAD">MAD</option>
            </select>
          </div>
          <input type="url" value={c.checkoutUrl} onChange={(e) => onUpdate('checkoutUrl', e.target.value)} placeholder="Lien de paiement" className={inputCls} />
        </div>
      )}

      {block.type === 'booking' && (
        <div className="space-y-2">
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={inputCls} />
          <input type="text" value={c.subtitle} onChange={(e) => onUpdate('subtitle', e.target.value)} placeholder="Durée / description" className={inputCls} />
          <input type="url" value={c.calendarUrl} onChange={(e) => onUpdate('calendarUrl', e.target.value)} placeholder="Lien Calendly / Cal.com" className={inputCls} />
        </div>
      )}

      {block.type === 'faq' && (
        <div className="space-y-2">
          {(c.items || []).map((item, i) => (
            <div key={i} className="p-2 bg-slate-50 rounded-lg border border-slate-200 space-y-1">
              <input value={item.q} onChange={(e) => { const items = [...c.items]; items[i] = { ...item, q: e.target.value }; onUpdate('items', items) }} placeholder="Question" className={inputCls} />
              <textarea rows={2} value={item.a} onChange={(e) => { const items = [...c.items]; items[i] = { ...item, a: e.target.value }; onUpdate('items', items) }} placeholder="Réponse" className={inputCls} />
              <button onClick={() => onUpdate('items', c.items.filter((_, idx) => idx !== i))} className="text-[10px] text-red-500">Supprimer</button>
            </div>
          ))}
          <button onClick={() => onUpdate('items', [...(c.items || []), { q: '', a: '' }])} className="w-full py-2 border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-500 hover:border-[#006b5f]">+ Ajouter une question</button>
        </div>
      )}

      {block.type === 'countdown' && (
        <div className="space-y-2">
          <input type="text" value={c.title} onChange={(e) => onUpdate('title', e.target.value)} placeholder="Titre" className={inputCls} />
          <input type="datetime-local" value={c.targetDate} onChange={(e) => onUpdate('targetDate', e.target.value)} className={inputCls} />
          <input type="url" value={c.buttonUrl} onChange={(e) => onUpdate('buttonUrl', e.target.value)} placeholder="Lien du bouton (optionnel)" className={inputCls} />
        </div>
      )}

      {block.type === 'review' && (
        <div className="space-y-2">
          <input type="text" value={c.author} onChange={(e) => onUpdate('author', e.target.value)} placeholder="Nom du client" className={inputCls} />
          <textarea rows={3} value={c.text} onChange={(e) => onUpdate('text', e.target.value)} placeholder="Texte de l'avis" className={inputCls} />
          <select value={c.rating} onChange={(e) => onUpdate('rating', Number(e.target.value))} className={inputCls}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoiles</option>)}
          </select>
        </div>
      )}

      {block.type === 'embed' && (
        <textarea rows={2} value={c.embedUrl} onChange={(e) => onUpdate('embedUrl', e.target.value)} placeholder="URL à intégrer (iframe)" className={inputCls} />
      )}

      {block.type === 'pdf' && (
        <div className="space-y-2">
          <button onClick={onUploadImage} disabled={isUploading}
            className="w-full flex items-center justify-center gap-1 py-2 border-2 border-dashed border-slate-200 rounded-lg hover:border-[#006b5f] text-xs text-slate-500">
            {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            {c.url ? 'Changer le fichier' : 'Uploader un PDF'}
          </button>
          <input type="text" value={c.label} onChange={(e) => onUpdate('label', e.target.value)} placeholder="Nom affiché" className={inputCls} />
        </div>
      )}

      {block.type === 'text_divider' && (
        <input type="text" value={c.label} onChange={(e) => onUpdate('label', e.target.value)} placeholder="Texte du séparateur" className={inputCls} />
      )}
    </div>
  )
}

function CountdownTimer({ target }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(target).getTime() - Date.now()))
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(target).getTime() - Date.now())), 1000)
    return () => clearInterval(t)
  }, [target])
  const d = Math.floor(left / 86400000), h = Math.floor((left % 86400000) / 3600000)
  const m = Math.floor((left % 3600000) / 60000), s = Math.floor((left % 60000) / 1000)
  return (
    <div className="flex justify-center gap-2 text-center">
      {[[d, 'j'], [h, 'h'], [m, 'm'], [s, 's']].map(([v, l]) => (
        <div key={l} className="bg-white/15 rounded-lg px-2.5 py-1.5 min-w-[46px]">
          <div className="text-base font-bold">{String(v).padStart(2, '0')}</div>
          <div className="text-[9px] opacity-70">{l}</div>
        </div>
      ))}
    </div>
  )
}

/* ============================================
   APERÇU LIVE (ancienne version, conservée pour compatibilité :
   l'éditeur utilise maintenant PagePublic en mode aperçu)
   ============================================ */
export function LivePreview({ pageInfo, blocks, theme, font, shape, settings }) {
  const textAlign = { left: 'text-left', center: 'text-center', right: 'text-right' }[settings.textAlign || 'center']

  const hasBgImage = !!settings.bgImage
  const overlay = (settings.bgOverlay ?? 40) / 100

  const bgStyle = hasBgImage
    ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay})), url(${settings.bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : { background: theme.bg }

  const buttonStyle = {
    solid: { background: theme.accent, color: theme.text },
    outline: { border: `2px solid ${theme.accent}`, color: theme.accent, background: 'transparent' },
    glass: { background: 'rgba(255,255,255,0.15)', color: theme.text, backdropFilter: 'blur(10px)' },
  }[settings.buttonStyle || 'solid']

  const now = new Date()
  const visibleBlocks = blocks.filter((b) => {
    if (b.type !== 'link' || !b.content?.scheduled) return true
    if (b.content.startAt && now < new Date(b.content.startAt)) return false
    if (b.content.endAt && now > new Date(b.content.endAt)) return false
    return true
  })

  return (
    <div style={{ ...bgStyle, fontFamily: font.family }} className={`min-h-[600px] ${textAlign}`}>
      <ProfileHeader pageInfo={pageInfo} settings={settings} theme={theme} />

      <div className="px-6 pb-6 mt-6 space-y-3">
        {visibleBlocks.map((block) => {
          const c = block.content || {}
          return (
            <div key={block.id}>
              {block.type === 'header' && (
                <div style={{ color: theme.text }}>
                  {c.title && <p className="font-bold text-base">{c.title}</p>}
                  {c.subtitle && <p className="text-xs opacity-70 mt-1">{c.subtitle}</p>}
                </div>
              )}
              {block.type === 'link' && (() => {
                const p = getPlatform(c.icon); const PIcon = p.icon
                return (
                  <a href={c.url || '#'} target="_blank" rel="noreferrer" className="block w-full py-3 px-4 font-semibold text-sm transition-all hover:scale-[1.02]" style={{ ...buttonStyle, borderRadius: shape.radius }}>
                    <div className="flex items-center gap-3">
                      <PIcon className="w-4 h-4" />
                      <span className="flex-1 text-left">{c.title || 'Lien'}</span>
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </div>
                  </a>
                )
              })()}
              {block.type === 'social' && (
                <SocialLink content={c} settings={settings} theme={theme} />
              )}
              {block.type === 'image' && c.url && (
                <div className="overflow-hidden" style={{ borderRadius: shape.radius }}>
                  <img src={c.url} alt="" className="w-full h-auto" />
                  {c.caption && <p className="text-[10px] opacity-70 mt-1" style={{ color: theme.text }}>{c.caption}</p>}
                </div>
              )}
              {block.type === 'video' && <VideoEmbed url={c.url} radius={shape.radius} />}
              {block.type === 'map' && c.address && (
                <div className="overflow-hidden" style={{ borderRadius: shape.radius, height: '120px' }}>
                  <iframe title="map" width="100%" height="100%" style={{ border: 0 }} loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}&output=embed`} />
                </div>
              )}
              {block.type === 'contact' && (
                <div className="p-4" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius }}>
                  <p className="font-semibold text-sm mb-1" style={{ color: theme.text }}>{c.title || 'Contact'}</p>
                  <p className="text-xs opacity-70 mb-3" style={{ color: theme.text }}>{c.subtitle}</p>
                  <div className="space-y-2">
                    <div className="h-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)' }} />
                    <button className="w-full py-2 text-xs font-semibold" style={{ ...buttonStyle, borderRadius: shape.radius }}>Envoyer</button>
                  </div>
                </div>
              )}
              {block.type === 'email_capture' && (
                <div className="p-4" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius }}>
                  <p className="font-semibold text-sm mb-1 flex items-center gap-1.5" style={{ color: theme.text }}><Mail className="w-3.5 h-3.5" />{c.title || 'Newsletter'}</p>
                  {c.subtitle && <p className="text-xs opacity-70 mb-3" style={{ color: theme.text }}>{c.subtitle}</p>}
                  <div className="space-y-2">
                    <div className="h-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)' }} />
                    <button className="w-full py-2 text-xs font-semibold" style={{ ...buttonStyle, borderRadius: shape.radius }}>{c.buttonLabel || "S'inscrire"}</button>
                  </div>
                </div>
              )}
              {block.type === 'shop' && (
                <div className="overflow-hidden text-left" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius }}>
                  {c.image && <img src={c.image} alt="" className="w-full h-28 object-cover" />}
                  <div className="p-3 space-y-1" style={{ color: theme.text }}>
                    <p className="font-semibold text-sm">{c.name || 'Produit'}</p>
                    <p className="text-xs opacity-70">{c.price ? `${c.price} ${c.currency || 'EUR'}` : ''}</p>
                    <button className="w-full mt-2 py-2 text-xs font-semibold flex items-center justify-center gap-1.5" style={{ ...buttonStyle, borderRadius: shape.radius }}>
                      <ShoppingBag className="w-3.5 h-3.5" /> Acheter
                    </button>
                  </div>
                </div>
              )}
              {block.type === 'booking' && (
                <div className="p-4 text-left" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius, color: theme.text }}>
                  <p className="font-semibold text-sm flex items-center gap-1.5"><Calendar className="w-4 h-4" />{c.title || 'Réserver un créneau'}</p>
                  {c.subtitle && <p className="text-xs opacity-70 mt-0.5">{c.subtitle}</p>}
                </div>
              )}
              {block.type === 'faq' && (
                <div className="text-left space-y-2">
                  {(c.items || []).map((item, i) => (
                    <details key={i} className="p-3" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius, color: theme.text }}>
                      <summary className="text-sm font-semibold cursor-pointer flex items-center gap-1.5"><HelpCircle className="w-4 h-4 shrink-0" />{item.q || 'Question'}</summary>
                      <p className="text-xs opacity-70 mt-2">{item.a}</p>
                    </details>
                  ))}
                </div>
              )}
              {block.type === 'countdown' && (
                <div className="p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius, color: theme.text }}>
                  {c.title && <p className="text-sm font-semibold flex items-center justify-center gap-1.5"><Timer className="w-4 h-4" />{c.title}</p>}
                  {c.targetDate && <CountdownTimer target={c.targetDate} />}
                  {c.buttonUrl && <button className="w-full mt-2 py-2 text-xs font-semibold" style={{ ...buttonStyle, borderRadius: shape.radius }}>En profiter</button>}
                </div>
              )}
              {block.type === 'review' && (
                <div className="p-4 text-left space-y-1" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius, color: theme.text }}>
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5" fill={i < (c.rating || 5) ? 'currentColor' : 'none'} />)}</div>
                  <p className="text-xs italic opacity-90">"{c.text}"</p>
                  {c.author && <p className="text-[11px] font-semibold opacity-70">— {c.author}</p>}
                </div>
              )}
              {block.type === 'embed' && c.embedUrl && (
                <div className="overflow-hidden" style={{ borderRadius: shape.radius, height: 200 }}>
                  <iframe title="embed" src={c.embedUrl} width="100%" height="100%" style={{ border: 0 }} loading="lazy" />
                </div>
              )}
              {block.type === 'pdf' && (
                <div className="p-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: shape.radius, color: theme.text }}>
                  <FileText className="w-4 h-4" /><span className="text-sm">{c.label || 'Document PDF'}</span>
                </div>
              )}
              {block.type === 'text_divider' && (
                <div className="flex items-center gap-3 py-1" style={{ color: theme.text }}>
                  <div className="flex-1 h-px bg-current opacity-20" /><span className="text-xs opacity-60">{c.label}</span><div className="flex-1 h-px bg-current opacity-20" />
                </div>
              )}
              {block.type === 'separator' && (
                <div style={{ height: { sm: '8px', md: '16px', lg: '32px' }[c.size] || '16px' }} />
              )}
              {block.type === 'music' && (() => {
                const p = getPlatform(c.platform)
                const PIcon = p.icon
                return (
                  <a href={c.url || '#'} target="_blank" rel="noreferrer" className="block w-full py-3 px-4 font-semibold text-sm" style={{ ...buttonStyle, borderRadius: shape.radius }}>
                    <div className="flex items-center gap-3">
                      <PIcon className="w-4 h-4" />
                      <span className="flex-1 text-left">Écouter sur {p.label}</span>
                      <Play className="w-3 h-3 opacity-50" />
                    </div>
                  </a>
                )
              })()}
            </div>
          )
        })}

        <p className="text-[10px] pt-6 opacity-50" style={{ color: theme.text }}>Propulsé par Tapio</p>
      </div>
    </div>
  )
}